// =============================================================================
// VandieZ admin-web -- Role detail (/roles/:roleId)
// =============================================================================
// Edit a role's name, description, and (for non-system roles) the
// permission set.  System roles' permission edits are blocked at the
// API gate so the form disables those checkboxes accordingly.
// =============================================================================

import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { permissionsAdminApi, rolesAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Permission, Role, RoleUpdatePayload } from "@/types/rbac";

export function RoleDetailPage(): React.JSX.Element {
  const { roleId } = useParams<{ roleId: string }>();
  const { accessToken } = useAuth();

  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());

  const fetchAll = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken || !roleId) {return;}
      setLoading(true);
      setError(null);
      try {
        // Roles endpoint doesn't have a singular GET (the list returns
        // everything with eager perms), so fetch the list and pluck.
        const [rolesRes, permsRes] = await Promise.all([
          rolesAdminApi.list(accessToken, signal),
          permissionsAdminApi.list(accessToken, signal),
        ]);
        const found = rolesRes.roles.find((r) => r.id === roleId);
        if (!found) {
          setError("Role not found.");
          return;
        }
        setRole(found);
        setPermissions(permsRes.permissions);
        setName(found.name);
        setDescription(found.description);
        setSelectedPerms(new Set(found.permission_codes));
      } catch (e: unknown) {
        if (e instanceof ApiError) {setError(e.problem.detail);}
        else if (e instanceof Error && e.name === "AbortError") {/* unmount */}
        else {setError("Unexpected error. Try again.");}
      } finally {
        setLoading(false);
      }
    },
    [accessToken, roleId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchAll(c.signal);
    return () => { c.abort(); };
  }, [fetchAll]);

  const togglePerm = (code: string): void => {
    if (role?.is_system) {return;}
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {next.delete(code);}
      else {next.add(code);}
      return next;
    });
  };

  const save = async (): Promise<void> => {
    if (!accessToken || !roleId || role === null) {return;}
    setSaving(true);
    setError(null);
    const payload: RoleUpdatePayload = {};
    if (name !== role.name) {payload.name = name;}
    if (description !== role.description) {payload.description = description;}
    if (!role.is_system) {
      payload.permission_codes = Array.from(selectedPerms);
    }
    try {
      const updated = await rolesAdminApi.update(accessToken, roleId, payload);
      setRole(updated);
      setSelectedPerms(new Set(updated.permission_codes));
    } catch (e: unknown) {
      if (e instanceof ApiError) {setError(e.problem.detail);}
      else {setError("Save failed.");}
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    role !== null &&
    (name !== role.name ||
      description !== role.description ||
      (!role.is_system && diffSet(selectedPerms, new Set(role.permission_codes))));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Role detail"
        description="Edit a role's name, description, and permission set."
        actions={
          <Link
            to="/roles"
            className="text-sm text-muted-foreground hover:underline flex items-center"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Roles
          </Link>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading || role === null ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{role.name}</CardTitle>
                    <CardDescription>
                      {role.is_system
                        ? "System role — permission set is canonical (managed by migration)."
                        : "Admin-created role — fully editable."}
                    </CardDescription>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs uppercase tracking-wide ${
                      role.is_system
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary-subtle text-primary-subtle-foreground"
                    }`}
                  >
                    {role.is_system ? "system" : "custom"}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      Name
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => { setName(e.target.value); }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      Description
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => { setDescription(e.target.value); }}
                      rows={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Permissions ({selectedPerms.size})</CardTitle>
                <CardDescription>
                  {role.is_system
                    ? "Permissions are locked on system roles. Create a custom role to change the set."
                    : "Pick the permission codes this role should grant."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {permissions.map((p) => {
                    const selected = selectedPerms.has(p.code);
                    return (
                      <label
                        key={p.code}
                        className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
                          role.is_system
                            ? "opacity-70 cursor-not-allowed"
                            : "cursor-pointer hover:bg-muted/30"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selected}
                          onChange={() => { togglePerm(p.code); }}
                          disabled={role.is_system}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs">{p.code}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.description}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => void save()} disabled={!dirty || saving}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </>
        )}
    </div>
  );
}

function diffSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {return true;}
  for (const v of a) {
    if (!b.has(v)) {return true;}
  }
  return false;
}
